// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Search, Eye, DollarSign, Users, Video, Plus } from 'lucide-react';
import { toast } from 'sonner';
import VendorDetailsModal from './VendorDetailsModal';

interface VideoVendor {
  id: string;
  user_id: string;
  email: string;
  name: string;
  commission_rate?: number;
  created_at: string;
  membership_status: string;
  total_orders: number;
  total_revenue: number;
}

const VideoVendorsTab: React.FC = () => {
  const [vendors, setVendors] = useState<VideoVendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState<VideoVendor | null>(null);
  const [showVendorDetails, setShowVendorDetails] = useState(false);
  const [showCreateVendor, setShowCreateVendor] = useState(false);

  useEffect(() => {
    fetchVideoVendors();
  }, []);

  const fetchVideoVendors = async () => {
    try {
      setIsLoading(true);
      
      // Fetch all profiles first (we'll filter by membership below)
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select(`
          id,
          user_id,
          email,
          name,
          commission_rate,
          created_at
        `);

      if (profilesError) throw profilesError;

      // Get video vendor memberships separately
      const { data: memberships, error: membershipsError } = await supabase
        .from('profile_memberships')
        .select(`
          profile_id,
          membership_slug,
          is_active
        `)
        .eq('membership_slug', 'vendor_video')
        .eq('is_active', true);

      if (membershipsError) throw membershipsError;

      // Filter profiles that have video vendor membership
      const videoVendorProfiles = profiles?.filter(profile => 
        memberships?.some(membership => membership.profile_id === profile.id)
      ) || [];

      // Get order statistics for each vendor
      const vendorsWithStats = await Promise.all(
        videoVendorProfiles.map(async (profile) => {
          // Count total orders for this vendor
          const { count: orderCount } = await supabase
            .from('video_orders')
            .select('*', { count: 'exact', head: true })
            .eq('vendor_id', profile.id);

          // Calculate total revenue
          const { data: orders } = await supabase
            .from('video_orders')
            .select('total_amount_cents')
            .eq('vendor_id', profile.id)
            .eq('status', 'completed');

          const totalRevenue = orders?.reduce((sum, order) => sum + (order.total_amount_cents || 0), 0) || 0;

          // Check if this profile has video vendor membership
          const hasVideoMembership = memberships?.some(m => m.profile_id === profile.id);

          return {
            ...profile,
            total_orders: orderCount || 0,
            total_revenue: totalRevenue / 100, // Convert to dollars
            membership_status: hasVideoMembership ? 'active' : 'inactive'
          };
        })
      );

      setVendors(vendorsWithStats);
    } catch (error) {
      console.error('Error fetching video vendors:', error);
      toast.error('Failed to load video vendors');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVendorUpdate = (updatedVendor: VideoVendor) => {
    setVendors(vendors.map(vendor => 
      vendor.id === updatedVendor.id ? updatedVendor : vendor
    ));
  };

  const handleViewVendorDetails = (vendor: VideoVendor) => {
    setSelectedVendor(vendor);
    setShowVendorDetails(true);
  };

  const filteredVendors = vendors.filter(vendor =>
    vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading video vendors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Video Vendors</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{vendors.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <Video className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {vendors.reduce((sum, vendor) => sum + vendor.total_orders, 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${vendors.reduce((sum, vendor) => sum + vendor.total_revenue, 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Video Vendors ({filteredVendors.length})</CardTitle>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
              <Button onClick={() => setShowCreateVendor(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Vendor Account
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredVendors.length === 0 ? (
            <div className="text-center py-8">
              <Video className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'No vendors found matching your search.' : 'No video vendors found.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Orders</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVendors.map((vendor) => (
                  <TableRow key={vendor.id}>
                    <TableCell className="font-medium">
                      {vendor.name || 'N/A'}
                    </TableCell>
                    <TableCell>{vendor.email}</TableCell>
                    <TableCell>
                      {vendor.commission_rate ? `${vendor.commission_rate}%` : 'Not set'}
                    </TableCell>
                    <TableCell>{vendor.total_orders}</TableCell>
                    <TableCell>${vendor.total_revenue.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={vendor.membership_status === 'active' ? 'default' : 'secondary'}>
                        {vendor.membership_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(vendor.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewVendorDetails(vendor)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <VendorDetailsModal
        vendor={selectedVendor}
        open={showVendorDetails}
        onOpenChange={setShowVendorDetails}
        onVendorUpdate={handleVendorUpdate}
      />

    </div>
  );
};

export default VideoVendorsTab;